import EventEmitter from 'events';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { StrictMode } from 'react';
import ReactDOM, { Root } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';

import './index.css';
import { AuthContextProvider } from './contexts/authContext';
import { requestRefresh } from './helpers/refresh-rpc';
import { router } from './routes/routes';

export const authEventEmitter: EventEmitter = new EventEmitter();

const BACKOFF_KEY: string = 'authRefreshBackoffUntil';

const isInBackoff = (): boolean => {
  const until = parseInt(localStorage.getItem(BACKOFF_KEY) ?? '0', 10);
  return Number.isFinite(until) && Date.now() < until;
};

const RootComponent = (): JSX.Element => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        staleTime: 60_000,
        cacheTime: 5 * 60_000,
        retry: (failureCount, error: any) => {
          if (isInBackoff()) return false;
          const status = error?.response?.status;
          if (status === 401) return false;
          return failureCount < 2;
        },
        onError: async (error: any) => {
          const status = error?.response?.status;
          if (status === 401 && !isInBackoff() && requestRefresh) {
            const res = await requestRefresh();
            if (res === 'ok') {
              void queryClient.invalidateQueries();
            }
          }
        },
      },
    },
  });

  return (
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <AuthContextProvider>
          <RouterProvider router={router} />
        </AuthContextProvider>
      </QueryClientProvider>
    </StrictMode>
  );
};

const root: Root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(<RootComponent />);
